/**
 * HOST-SETS — the catalogue-wide "every plant has a purpose" table.
 *
 * This is the single, hand-editable source of the ATTRACTOR SETS (RequireSet[]) that tie insects and fish to
 * specific garden plants/decor. It lives apart from the bespoke creature defs so the whole mapping is readable
 * and tunable in ONE place (by the user in the Odds modals, or by Claude editing this file directly).
 *
 * GOAL: every plant in the catalogue appears in ≥1 creature's set, so no plant is dead weight — while keeping
 * the relationship many-to-many (a plant can attract several creatures; a creature can be attracted by any of
 * several plants). See `orphanPlants()` for the coverage check.
 *
 * These are the DEFAULT sets: `effectiveAppears`/`effectiveFishCond` merge them onto a creature's source cond
 * UNLESS an admin override for that creature exists (then the override — which the modal seeds FROM these — wins).
 *
 * Convention: most sets are `use: "both"` (a soft gate that also rewards planting more), with a modest `perExtra`
 * so stacking hosts keeps paying off. Use `use: "driver"` for a pure boost (never blocks) and `use: "gate"` for
 * a hard requirement with no stacking bonus.
 *
 * THE PREMIUM RULE — the one a future editor must not casually break. A `use: "both"`/`"gate"` set is a HARD GATE,
 * and a creature's gate sets are ANDed (scoreRequireSets), so EVERY gate set it carries must be satisfiable with
 * plants LEVELING can unlock — one free gate beside a premium-only one still paywalls it. Nine plants are
 * premium (real-money only; PREMIUM_PLANTS in level.ts): voidOrchid, auroraFern, lilyOfTheDead, waterDandelion,
 * vampiricPitcher, nightglowMushroom, bloodshadowRose, lightbloom, abyssBlossom. `isUnlocked` never grants them,
 * so a gate reachable ONLY through them paywalls the species — and with it the `full_cabinet` / `full_aquarium`
 * completion achievements, which ask for all 70. The trap is `mode: "all"`: one premium item in the recipe locks the
 * whole set, where in `mode: "any"` a single free item keeps it open. Five sets had drifted into exactly that
 * (voidwing, auroralace, bogwraith, moonscale, frostdart) and are now `any` with a free path; the premium plants
 * stay listed as the flavourful option. scripts/smoke.mjs asserts this for every insect and fish.
 */

import type { RequireSet } from "./requireSets";

/** insectId → its default attractor sets. Every catalogue plant appears in at least one of these (see
 *  orphanPlants / the Dev Menu "🌱 Plant coverage" readout). Themed: night-bloomers→moths, water plants→
 *  dragonflies, showy blooms→big butterflies, herbs→bees, succulents/cacti→beetles, ferns/moss→woodland
 *  crawlers, fantasy plants→fantasy creatures, premium dark plants→void/shadow creatures. */
export const INSECT_HOST_SETS: Record<string, RequireSet[]> = {
  cabbageWhite: [
    { mode: "any", target: "plant", use: "both", label: "brassica & border blooms", items: [{ id: "marigold", need: 1 }, { id: "pansy", need: 1 }, { id: "petunia", need: 1 }, { id: "nasturtium", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  sulphur: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "marigold", need: 1 }, { id: "poppy", need: 1 }, { id: "ranunculus", need: 1 }, { id: "sunflower", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  orangeTip: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "pansy", need: 1 }, { id: "sweetPea", need: 1 }, { id: "snapdragon", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  commonBlue: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "aster", need: 1 }, { id: "cosmos", need: 1 }, { id: "daisy", need: 1 }, { id: "chrysanthemum", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  painted: [
    { mode: "any", target: "plant", use: "both", label: "thistle-loving nectar seekers", items: [{ id: "aster", need: 1 }, { id: "zinnia", need: 1 }, { id: "chrysanthemum", need: 1 }, { id: "blackEyedSusan", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  peacock: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "hibiscus", need: 1 }, { id: "peony", need: 1 }, { id: "carnation", need: 1 }, { id: "gerbera", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  monarch: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "aster", need: 1 }, { id: "zinnia", need: 1 }, { id: "cosmos", need: 1 }, { id: "blackEyedSusan", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  morpho: [
    { mode: "all", target: "plant", use: "both", label: "tropical showpiece pair", items: [{ id: "hibiscus", need: 1 }, { id: "gerbera", need: 1 }], perExtra: 0.35 },
    { mode: "any", target: "plant", use: "driver", items: [{ id: "peony", need: 1 }, { id: "camellia", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  swallowtail: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "geranium", need: 1 }, { id: "petunia", need: 1 }, { id: "gladiolus", need: 1 }, { id: "hibiscus", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  luna: [
    { mode: "any", target: "plant", use: "both", label: "night-bloomers", items: [{ id: "morningGlory", need: 1 }, { id: "nightglowMushroom", need: 1 }, { id: "lightbloom", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  redAdmiral: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "aster", need: 1 }, { id: "chrysanthemum", need: 1 }, { id: "gerbera", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  gulfFritillary: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "cosmos", need: 1 }, { id: "zinnia", need: 1 }, { id: "hibiscus", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  smallCopper: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "daisy", need: 1 }, { id: "marigold", need: 1 }, { id: "blackEyedSusan", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  hollyBlue: [
    { mode: "all", target: "plant", use: "both", label: "holly & ivy", items: [{ id: "holly", need: 1 }, { id: "englishIvy", need: 1 }], perExtra: 0.3 },
  ],
  speckledWood: [
    { mode: "any", target: "plant", use: "both", label: "shade & fern glade", items: [{ id: "bostonFern", need: 1 }, { id: "maidenhairFern", need: 1 }, { id: "birdsNestFern", need: 1 }, { id: "mondoGrass", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  comma: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "azalea", need: 1 }, { id: "rhododendron", need: 1 }, { id: "hydrangeaBush", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  marbledWhite: [
    { mode: "any", target: "plant", use: "both", label: "meadow grasses & daisies", items: [{ id: "fountainGrass", need: 1 }, { id: "blueFescue", need: 1 }, { id: "daisy", need: 1 }, { id: "purpleMillet", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  viceroy: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "aster", need: 1 }, { id: "zinnia", need: 1 }, { id: "blackEyedSusan", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  zebraLongwing: [
    { mode: "all", target: "plant", use: "both", label: "tropical trellis", items: [{ id: "hibiscus", need: 1 }, { id: "morningGlory", need: 1 }], perExtra: 0.35 },
  ],
  mourningCloak: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "boxwood", need: 1 }, { id: "holly", need: 1 }, { id: "winterberry", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  tidewing: [
    { mode: "cluster", target: "plant", use: "both", label: "waterside reeds", anyOf: ["papyrus", "horsetail", "sedge", "waterDandelion"], count: 3, radius: 2.5, perExtra: 0.3 },
  ],
  prismwing: [
    { mode: "any", target: "plant", use: "both", label: "crystalline blooms", items: [{ id: "crystalbloom", need: 1 }, { id: "prismRose", need: 1 }, { id: "amethystThorn", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  lumenwing: [
    { mode: "any", target: "plant", use: "both", label: "glow flora", items: [{ id: "lumenCactus", need: 1 }, { id: "lightbloom", need: 1 }, { id: "crystalbloom", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  frostwing: [
    { mode: "all", target: "plant", use: "both", label: "frozen pair", items: [{ id: "frostfern", need: 1 }, { id: "glacierlily", need: 1 }], perExtra: 0.35 },
  ],
  thornwing: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "amethystThorn", need: 1 }, { id: "bloodshadowRose", need: 1 }, { id: "vampiricPitcher", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  stainedglass: [
    { mode: "any", target: "plant", use: "both", label: "jewel-toned showblooms", items: [{ id: "prismRose", need: 1 }, { id: "crystalbloom", need: 1 }, { id: "gladiolus", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  auroralace: [
    // WAS an `all` pair auroraFern + glacierlily — and auroraFern is premium, so the gate could not be opened by
    // leveling at all. Now "two plants of the aurora glade, any two": a glacierlily PAIR opens it for free, and an
    // auroraFern still counts (and is the cheapest half of a copy, since one of each is also two).
    { mode: "any", target: "plant", use: "both", label: "aurora glade", items: [{ id: "auroraFern", need: 1 }, { id: "glacierlily", need: 1 }, { id: "frostfern", need: 1 }], min: 2, perExtra: 0.35 },
    { mode: "any", target: "plant", use: "driver", items: [{ id: "frostfern", need: 1 }, { id: "cloudPuff", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  sporewing: [
    { mode: "any", target: "plant", use: "both", label: "spore & moss floor", items: [{ id: "nightglowMushroom", need: 1 }, { id: "lanternMoss", need: 1 }, { id: "pearlFern", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  emberflutter: [
    { mode: "any", target: "plant", use: "both", label: "ember flora", items: [{ id: "emberbloom", need: 1 }, { id: "phoenixLily", need: 1 }, { id: "magmaSucculent", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  voidwing: [
    // amethystThorn is the FREE way in. The other three are premium (shop-only), so without it this "any" gate
    // named nothing leveling can unlock and the Voidwing — plus `full_cabinet` behind it — was paywalled.
    { mode: "any", target: "plant", use: "both", label: "void garden", items: [{ id: "amethystThorn", need: 1 }, { id: "voidOrchid", need: 1 }, { id: "abyssBlossom", need: 1 }, { id: "bloodshadowRose", need: 1 }], min: 1, perExtra: 0.35 },
    { mode: "cluster", target: "plant", use: "driver", anyOf: ["voidOrchid", "abyssBlossom", "nightglowMushroom", "vampiricPitcher"], count: 3, radius: 2.5, perExtra: 0.35 },
  ],
  ladybug: [
    { mode: "any", target: "plant", use: "both", label: "aphid-prone tender growth", items: [{ id: "rose", need: 1 }, { id: "miniRoseBush", need: 1 }, { id: "sweetPea", need: 1 }, { id: "nasturtium", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  ant: [
    { mode: "any", target: "plant", use: "driver", items: [{ id: "peony", need: 1 }, { id: "carnation", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  woodlouse: [
    { mode: "any", target: "plant", use: "both", label: "damp leaf litter", items: [{ id: "bostonFern", need: 1 }, { id: "buttonFern", need: 1 }, { id: "lanternMoss", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  shieldBug: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "boxwood", need: 1 }, { id: "azalea", need: 1 }, { id: "pepperPlant", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  leafBeetle: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "hydrangeaBush", need: 1 }, { id: "rhododendron", need: 1 }, { id: "pothos", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  bumblebee: [
    { mode: "any", target: "plant", use: "both", label: "cottage nectar", items: [{ id: "foxglove", need: 1 }, { id: "delphinium", need: 1 }, { id: "lavender", need: 1 }, { id: "snapdragon", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  grasshopper: [
    { mode: "any", target: "plant", use: "both", label: "tall grasses", items: [{ id: "pampasGrass", need: 1 }, { id: "fountainGrass", need: 1 }, { id: "purpleMillet", need: 1 }, { id: "blueFescue", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  firefly: [
    { mode: "any", target: "plant", use: "both", label: "dusk glow garden", items: [{ id: "morningGlory", need: 1 }, { id: "honeybell", need: 1 }, { id: "lanternMoss", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  roseChafer: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "rose", need: 1 }, { id: "miniRoseBush", need: 1 }, { id: "peony", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  cricket: [
    { mode: "any", target: "plant", use: "driver", items: [{ id: "mondoGrass", need: 1 }, { id: "sedge", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  dragonfly: [
    { mode: "cluster", target: "plant", use: "both", label: "pond margin", anyOf: ["papyrus", "horsetail", "sedge", "iris", "waterDandelion"], count: 3, radius: 2.5, perExtra: 0.3 },
  ],
  // The Stag Beetle now lives on TREE BARK — its `treeCling` behaviour clings to a trunk — so it's gated on a
  // TREE DECOR rather than plants (a garden without a tree has nowhere for it to cling). The shrubs it used to
  // want (boxwood/holly/winterberry) are still referenced by other insects, so this move orphans no plant.
  stagBeetle: [
    { mode: "any", target: "decor", use: "both", label: "a tree to climb", items: [{ id: "oakTree", need: 1 }, { id: "birchTree", need: 1 }, { id: "mapleTree", need: 1 }, { id: "cherryTree", need: 1 }, { id: "pineTree", need: 1 }, { id: "poplarTree", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  mantis: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "snakePlant", need: 1 }, { id: "lavenderShrub", need: 1 }, { id: "gardenia", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  rhinoBeetle: [
    { mode: "all", target: "plant", use: "both", label: "arid succulent bed", items: [{ id: "agave", need: 1 }, { id: "barrelCactus", need: 1 }], perExtra: 0.3 },
    { mode: "any", target: "plant", use: "driver", items: [{ id: "pricklyPear", need: 1 }, { id: "goldenCactus", need: 1 }, { id: "pincushionCactus", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  jewelScarab: [
    { mode: "any", target: "plant", use: "both", label: "desert succulents", items: [{ id: "echeveria", need: 1 }, { id: "jade", need: 1 }, { id: "zebraCactus", need: 1 }, { id: "burrosTail", need: 1 }, { id: "aloe", need: 1 }, { id: "haworthia", need: 1 }], min: 1, perExtra: 0.3 },
    { mode: "any", target: "plant", use: "driver", label: "rosette succulents", items: [{ id: "henChicks", need: 1 }, { id: "sempervivum", need: 1 }, { id: "stringPearls", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  emberwing: [
    { mode: "all", target: "plant", use: "both", label: "ember pair", items: [{ id: "emberbloom", need: 1 }, { id: "magmaSucculent", need: 1 }], perExtra: 0.35 },
  ],
  crystalBeetle: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "crystalbloom", need: 1 }, { id: "amethystThorn", need: 1 }, { id: "prismRose", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  sporeback: [
    { mode: "any", target: "plant", use: "both", label: "fungal shade", items: [{ id: "nightglowMushroom", need: 1 }, { id: "lanternMoss", need: 1 }, { id: "staghornFern", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  prismDragonfly: [
    { mode: "cluster", target: "plant", use: "both", label: "enchanted waterside", anyOf: ["glacierlily", "waterDandelion", "lilyOfTheDead", "papyrus"], count: 3, radius: 2.5, perExtra: 0.35 },
  ],
  lanternWisp: [
    { mode: "any", target: "plant", use: "both", label: "lantern flora", items: [{ id: "cloudPuff", need: 1 }, { id: "willowisp", need: 1 }, { id: "honeybell", need: 1 }, { id: "glassvine", need: 1 }, { id: "cometvine", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  honeybee: [
    { mode: "any", target: "plant", use: "both", label: "herb & pollinator patch", items: [{ id: "rosemary", need: 1 }, { id: "lavender", need: 1 }, { id: "gardenia", need: 1 }, { id: "freesia", need: 1 }, { id: "crocus", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  hoverfly: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "cosmos", need: 1 }, { id: "marigold", need: 1 }, { id: "daisy", need: 1 }], min: 1, perExtra: 0.2 },
    { mode: "any", target: "plant", use: "driver", label: "spring bulbs", items: [{ id: "tulip", need: 1 }, { id: "hyacinth", need: 1 }, { id: "daffodil", need: 1 }, { id: "crocus", need: 1 }, { id: "allium", need: 1 }, { id: "bluebell", need: 1 }, { id: "anemone", need: 1 }, { id: "iris", need: 1 }, { id: "freesia", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  lacewing: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "poppy", need: 1 }, { id: "ranunculus", need: 1 }, { id: "petunia", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  cranefly: [
    { mode: "any", target: "plant", use: "both", label: "damp turf & grass", items: [{ id: "mondoGrass", need: 1 }, { id: "sedge", need: 1 }, { id: "creepingJenny", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  mayfly: [
    { mode: "cluster", target: "plant", use: "driver", label: "streamside", anyOf: ["horsetail", "sedge", "papyrus"], count: 3, radius: 2.5, perExtra: 0.25 },
  ],
  hawkmoth: [
    { mode: "any", target: "plant", use: "both", label: "night-scented tube flowers", items: [{ id: "foxglove", need: 1 }, { id: "morningGlory", need: 1 }, { id: "honeybell", need: 1 }, { id: "gardenia", need: 1 }], min: 1, perExtra: 0.3 },
  ],
  aphid: [
    { mode: "any", target: "plant", use: "driver", items: [{ id: "rose", need: 1 }, { id: "sweetPea", need: 1 }, { id: "nasturtium", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  slug: [
    { mode: "any", target: "plant", use: "both", label: "tender leaves", items: [{ id: "pothos", need: 1 }, { id: "philodendron", need: 1 }, { id: "stringHearts", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  snail: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "ivyGeranium", need: 1 }, { id: "trailingLobelia", need: 1 }, { id: "wateringIvy", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  millipede: [
    { mode: "any", target: "plant", use: "both", label: "fern floor detritus", items: [{ id: "asparagusFern", need: 1 }, { id: "buttonFern", need: 1 }, { id: "pearlFern", need: 1 }, { id: "englishIvy", need: 1 }], min: 1, perExtra: 0.2 },
  ],
};

/** fishId → its default attractor sets (host plants NEAR the water). Only ~15 fish are plant-tied; the rest
 *  are gated by water/weather/rarity alone. Water/bog plants + fantasy pond plants feature here. */
export const FISH_HOST_SETS: Record<string, RequireSet[]> = {
  // THE FIRST TWO FISH ARE `use: "driver"`, NOT `"both"`, AND THAT IS THE WHOLE POINT. A "both" set is a HARD
  // GATE (requireSets.ts: copies >= 1 or the fish cannot bite), so the Pumpkinseed — documented in fishConds.ts
  // as "the one guaranteed bite in the game" — was in fact gated behind THREE reeds clustered within radius 2.5,
  // and the Bluegill behind a named waterside plant. A brand-new garden has neither, so the roster's two starter
  // fish were both unreachable on the free rig and the starting pond was EMPTY. As drivers the reed bank still
  // pays: plant it and these two bite far more often. It just no longer decides whether they exist.
  // Do not restore "both" here without giving the early game a different guaranteed fish.
  sunfish: [
    { mode: "cluster", target: "plant", use: "driver", label: "sunny reed bank", anyOf: ["papyrus", "sedge", "iris"], count: 3, radius: 2.5, perExtra: 0.2 },
  ],
  bluegill: [
    { mode: "any", target: "plant", use: "driver", label: "weedy shallows", items: [{ id: "sedge", need: 1 }, { id: "horsetail", need: 1 }, { id: "creepingJenny", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  rudd: [
    { mode: "any", target: "plant", use: "both", label: "weedy margin", items: [{ id: "sedge", need: 1 }, { id: "iris", need: 1 }, { id: "creepingJenny", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  carp: [
    { mode: "any", target: "plant", use: "both", label: "muddy margin plants", items: [{ id: "papyrus", need: 1 }, { id: "iris", need: 1 }, { id: "gladiolus", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  koi: [
    { mode: "cluster", target: "plant", use: "both", label: "ornamental pond edge", anyOf: ["iris", "gladiolus", "waterDandelion", "papyrus"], count: 3, radius: 2.5, perExtra: 0.25 },
  ],
  catfish: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "horsetail", need: 1 }, { id: "sedge", need: 1 }, { id: "trailingLobelia", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  eel: [
    { mode: "any", target: "plant", use: "both", label: "tangled bank roots", items: [{ id: "wateringIvy", need: 1 }, { id: "creepingJenny", need: 1 }, { id: "horsetail", need: 1 }], min: 1, perExtra: 0.25 },
  ],
  pike: [
    { mode: "cluster", target: "plant", use: "both", label: "reedy ambush cover", anyOf: ["papyrus", "horsetail", "sedge"], count: 3, radius: 2.5, perExtra: 0.25 },
  ],
  trout: [
    { mode: "any", target: "plant", use: "both", items: [{ id: "waterDandelion", need: 1 }, { id: "iris", need: 1 }, { id: "sedge", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  stoneLoach: [
    // deliberately NOT the Gudgeon's open "gravel riffle cover": this fish wants the shaded, overgrown
    // margin it can hide under by day, so the two bottom fish are gated by different bits of the garden
    { mode: "any", target: "plant", use: "both", label: "shaded stony margin", items: [{ id: "creepingJenny", need: 1 }, { id: "wateringIvy", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  gudgeon: [
    { mode: "any", target: "plant", use: "both", label: "gravel riffle cover", items: [{ id: "horsetail", need: 1 }, { id: "sedge", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  bleak: [
    { mode: "any", target: "plant", use: "both", label: "open-water margin", items: [{ id: "waterDandelion", need: 1 }, { id: "iris", need: 1 }], min: 1, perExtra: 0.2 },
  ],
  bogwraith: [
    // WAS an `all` pair of two PREMIUM plants (lilyOfTheDead + waterDandelion) — unopenable by leveling, which
    // paywalled the fish and `full_aquarium` with it. Now any two bog plants: a horsetail + papyrus margin does it.
    { mode: "any", target: "plant", use: "both", label: "haunted bog", items: [{ id: "lilyOfTheDead", need: 1 }, { id: "waterDandelion", need: 1 }, { id: "horsetail", need: 1 }, { id: "papyrus", need: 1 }], min: 2, perExtra: 0.35 },
  ],
  dewdrop: [
    { mode: "any", target: "plant", use: "both", label: "glimmer waterside", items: [{ id: "glacierlily", need: 1 }, { id: "waterDandelion", need: 1 }, { id: "lilyOfTheDead", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  moonscale: [
    // lilyOfTheDead is premium, so the old `all` pair was shop-only. glacierlily (fantasy, licensable) and the
    // moonflower-white morningGlory now open it on their own; the lily is still the flavourful half.
    { mode: "any", target: "plant", use: "both", label: "moonlit pool", items: [{ id: "lilyOfTheDead", need: 1 }, { id: "glacierlily", need: 1 }, { id: "morningGlory", need: 1 }], min: 2, perExtra: 0.35 },
  ],
  prismscale: [
    { mode: "cluster", target: "plant", use: "both", label: "prismatic shallows", anyOf: ["glacierlily", "waterDandelion", "iris", "papyrus"], count: 3, radius: 2.5, perExtra: 0.35 },
  ],
  glimmerkoi: [
    { mode: "any", target: "plant", use: "both", label: "enchanted koi pond", items: [{ id: "waterDandelion", need: 1 }, { id: "glacierlily", need: 1 }, { id: "iris", need: 1 }], min: 1, perExtra: 0.35 },
  ],
  frostdart: [
    // same premium lock as the Moonscale: the old `all` pair needed lilyOfTheDead. frostfern + glacierlily is the
    // free frozen pair; the lily still counts toward the two.
    { mode: "any", target: "plant", use: "both", label: "frozen spring", items: [{ id: "glacierlily", need: 1 }, { id: "frostfern", need: 1 }, { id: "lilyOfTheDead", need: 1 }], min: 2, perExtra: 0.35 },
  ],
  aetherling: [
    { mode: "cluster", target: "plant", use: "driver", label: "aether pool", anyOf: ["glacierlily", "lilyOfTheDead", "waterDandelion", "papyrus"], count: 3, radius: 2.5, perExtra: 0.35 },
  ],
};

/** Merge a creature's source sets with its authored host-sets (host-sets appended; dedup by identity is not
 *  needed since sources rarely define sets). Returns undefined when neither exists. */
export function withHostSets(sourceSets: RequireSet[] | undefined, hostSets: RequireSet[] | undefined): RequireSet[] | undefined {
  const a = sourceSets ?? [];
  const b = hostSets ?? [];
  if (!a.length && !b.length) return undefined;
  return [...a, ...b];
}

/** Every plant/decor id referenced by any set in a table (for coverage checks). */
export function referencedIds(table: Record<string, RequireSet[]>): Set<string> {
  const ids = new Set<string>();
  for (const sets of Object.values(table)) {
    for (const s of sets) {
      if (s.mode === "cluster") { (s.anyOf ?? []).forEach((x) => ids.add(x)); if (s.species) ids.add(s.species); }
      else for (const it of s.items ?? []) ids.add(it.id);
    }
  }
  return ids;
}

/** Plant ids from `allPlantIds` that NO creature references — the "orphans" that lack a purpose. */
export function orphanPlants(allPlantIds: string[]): string[] {
  const covered = new Set<string>([...referencedIds(INSECT_HOST_SETS), ...referencedIds(FISH_HOST_SETS)]);
  return allPlantIds.filter((id) => !covered.has(id));
}
